import React, { useState, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import Button from '@material-ui/core/Button'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import SearchIcon from '@material-ui/icons/Search'
import GetAppIcon from '@material-ui/icons/GetApp'
import * as XLSX from 'xlsx'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import IconButton from '@material-ui/core/IconButton'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import { format } from 'date-fns'

var useStyles = makeStyles(function(theme) {
  return {
    kpiCard: {
      padding: '16px 18px',
      border: '1px solid #e8ecf4',
      borderRadius: 12,
    },
    kpiValue: { fontSize: 26, fontWeight: 800, fontFamily: 'monospace', marginBottom: 4 },
    kpiLabel: { fontSize: 11, fontWeight: 600, color: '#666' },
    kpiBar: { height: 2, borderRadius: 999, background: '#f0f0f0', marginTop: 10 },
    controls: {
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      flexWrap: 'wrap',
      borderBottom: '1px solid #f0f0f0',
    },
    search: { flex: 1, minWidth: 200 },
    count: { fontSize: 12, color: '#777' },
    exportBtn: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: 12,
      color: '#666',
      border: '1px solid #e0e0e0',
      borderRadius: 7,
      padding: '5px 14px',
      '&:hover': { background: 'rgba(99,102,241,0.06)', color: '#6366f1', borderColor: '#c7d2fe' },
    },
    tableWrap: { overflowX: 'auto' },
    th: {
      fontSize: 11,
      fontWeight: 700,
      color: '#444',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      whiteSpace: 'nowrap',
      padding: '10px 16px',
      background: '#fafafa',
      cursor: 'pointer',
      userSelect: 'none',
    },
    thNoSort: {
      fontSize: 11,
      fontWeight: 700,
      color: '#444',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      whiteSpace: 'nowrap',
      padding: '10px 16px',
      background: '#fafafa',
    },
    td: { padding: '11px 16px', fontSize: 13 },
    trEven: { background: '#fff', '&:hover': { background: '#f8faff' } },
    trOdd: { background: '#fafbff', '&:hover': { background: '#f8faff' } },
    avatar: {
      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700,
    },
    chip: { height: 20, fontSize: 10, fontWeight: 700 },
  }
})

function fmtCost(v) {
  if (v == null || v === 0) return '$0.0000'
  if (v < 0.01) return '$' + v.toFixed(5)
  return '$' + v.toFixed(4)
}

function shortId(id) {
  if (!id) return '—'
  if (id.length <= 14) return id
  return id.slice(0, 7) + '…' + id.slice(-5)
}

var EXPORT_HEADERS = ['User ID', 'First Name', 'Last Name', 'Consumer ID', 'Device ID', 'Conversations', 'Total Cost', 'Added to Cart', 'Checked Out', 'Last Seen']

function buildRows(users) {
  return users.map(function(u) {
    return [
      u.userId || '', u.firstName || '', u.lastName || '',
      u.consumerId || '', u.deviceId || '',
      u.sessionCount, u.totalCost.toFixed(6),
      u.addedToCart ? 'Yes' : 'No',
      u.checkedOut ? 'Yes' : 'No',
      u.lastSeen ? u.lastSeen.toISOString() : '',
    ]
  })
}

function csvField(v) {
  var s = v == null ? '' : String(v)
  if (s.search(/[",\r\n]/) >= 0) return '"' + s.replace(/"/g, '""') + '"'
  return s
}

function triggerDownload(blob, filename) {
  var url = URL.createObjectURL(blob)
  var a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function exportCSV(users) {
  var rows = [EXPORT_HEADERS].concat(buildRows(users))
  var csv = rows.map(function(r) { return r.map(csvField).join(',') }).join('\r\n')
  // UTF-8 BOM so Google Sheets opens without the encoding/import dialog
  var blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
  triggerDownload(blob, 'voice-users.csv')
}

function exportJSON(users) {
  var data = users.map(function(u) {
    return {
      user_id: u.userId || '',
      first_name: u.firstName || '',
      last_name: u.lastName || '',
      consumer_id: u.consumerId || '',
      device_id: u.deviceId || '',
      conversations: u.sessionCount,
      total_cost_usd: parseFloat(u.totalCost.toFixed(6)),
      added_to_cart: u.addedToCart,
      checked_out: u.checkedOut,
      last_seen: u.lastSeen ? u.lastSeen.toISOString() : null,
    }
  })
  var blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  triggerDownload(blob, 'voice-users.json')
}

function exportExcel(users) {
  var rows = [EXPORT_HEADERS].concat(buildRows(users))
  var ws = XLSX.utils.aoa_to_sheet(rows)
  var wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Voice Users')
  XLSX.writeFile(wb, 'voice-users.xlsx')
}

function SortIcon(props) {
  if (props.sortKey !== props.col) return null
  return props.sortDir === 'asc'
    ? <ArrowUpwardIcon style={{ fontSize: 11, marginLeft: 3, color: '#6366f1', verticalAlign: 'middle' }} />
    : <ArrowDownwardIcon style={{ fontSize: 11, marginLeft: 3, color: '#6366f1', verticalAlign: 'middle' }} />
}

function BoolChip(props) {
  var classes = useStyles()
  return (
    <Chip
      size="small"
      className={classes.chip}
      label={props.value ? (props.trueLabel || '✓ Yes') : (props.falseLabel || '✗ No')}
      style={{
        background: props.value ? (props.trueBg || 'rgba(52,211,153,0.12)') : '#f5f5f5',
        color: props.value ? (props.trueColor || '#34D399') : '#aaa',
      }}
    />
  )
}

export default function UsersTab(props) {
  var classes = useStyles()
  var sessions = (props.data && props.data.sessions) || []
  var loading = props.loading

  var [search, setSearch] = useState('')
  var [sortKey, setSortKey] = useState('lastSeen')
  var [sortDir, setSortDir] = useState('desc')
  var [expandedUsers, setExpandedUsers] = useState(new Set())
  var [exportAnchor, setExportAnchor] = useState(null)

  // Aggregate sessions → users
  var users = useMemo(function() {
    var map = {}
    for (var i = 0; i < sessions.length; i++) {
      var s = sessions[i]
      var uid = s.consumerId || s.deviceId || s.userId || s.id
      if (!map[uid]) {
        map[uid] = {
          userId: uid,
          firstName: s.firstName || '',
          lastName: s.lastName || '',
          consumerId: s.consumerId || '',
          deviceId: s.deviceId || '',
          isAnonymous: !s.consumerId,
          sessionCount: 0,
          totalCost: 0,
          addedToCart: false,
          checkedOut: false,
          cartSessionCount: 0,
          checkoutSessionCount: 0,
          lastSeen: null,
          sessions: [],
        }
      }
      var u = map[uid]
      u.sessions.push(s)
      u.sessionCount++
      u.totalCost += s.totalCost || 0
      if (s.addedToCart) { u.addedToCart = true; u.cartSessionCount++ }
      if (s.checkedOut) { u.checkedOut = true; u.checkoutSessionCount++ }
      if (s.startTime && (!u.lastSeen || s.startTime > u.lastSeen)) u.lastSeen = s.startTime
      if (s.firstName && !u.firstName) u.firstName = s.firstName
      if (s.lastName && !u.lastName) u.lastName = s.lastName
      if (s.consumerId && !u.consumerId) { u.consumerId = s.consumerId; u.isAnonymous = false }
    }
    Object.values(map).forEach(function(u) {
      u.sessions.sort(function(a, b) {
        var at = a.startTime ? a.startTime.getTime() : 0
        var bt = b.startTime ? b.startTime.getTime() : 0
        return bt - at
      })
    })
    return Object.values(map)
  }, [sessions])

  var filtered = useMemo(function() {
    var rows = users.slice()
    if (search.trim()) {
      var q = search.toLowerCase()
      rows = rows.filter(function(u) {
        return (u.userId || '').toLowerCase().includes(q) ||
               (u.firstName || '').toLowerCase().includes(q) ||
               (u.lastName || '').toLowerCase().includes(q) ||
               (u.consumerId || '').toLowerCase().includes(q) ||
               (u.deviceId || '').toLowerCase().includes(q)
      })
    }
    rows.sort(function(a, b) {
      var av = a[sortKey], bv = b[sortKey]
      if (sortKey === 'lastSeen') { av = av ? av.getTime() : 0; bv = bv ? bv.getTime() : 0 }
      if (sortKey === 'addedToCart' || sortKey === 'checkedOut' || sortKey === 'isAnonymous') {
        av = av ? 1 : 0; bv = bv ? 1 : 0
      }
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      return sortDir === 'asc' ? av - bv : bv - av
    })
    return rows
  }, [users, search, sortKey, sortDir])

  function handleSort(key) {
    if (sortKey === key) setSortDir(function(d) { return d === 'asc' ? 'desc' : 'asc' })
    else { setSortKey(key); setSortDir('desc') }
  }

  var totalUsers = users.length
  var authUsers = users.filter(function(u) { return !u.isAnonymous }).length
  var anonUsers = totalUsers - authUsers
  var cartUsers = users.filter(function(u) { return u.addedToCart }).length
  var checkoutUsers = users.filter(function(u) { return u.checkedOut }).length

  var kpis = [
    { label: 'Total Users',      value: totalUsers,    color: '#818CF8' },
    { label: 'Authenticated',    value: authUsers,     color: '#34D399' },
    { label: 'Anonymous',        value: anonUsers,     color: '#94A3B8' },
    { label: 'Added to Cart',    value: cartUsers,     color: '#34D399' },
    { label: 'Reached Checkout', value: checkoutUsers, color: '#FCD34D' },
  ]

  var COLS = [
    { key: '_expand',      label: '',              sortable: false, colStyle: { width: 36, padding: '0 4px' } },
    { key: 'name',         label: 'User',          sortable: false },
    { key: 'userId',       label: 'ID',             sortable: false },
    { key: 'deviceId',     label: 'Device ID',      sortable: false },
    { key: 'isAnonymous',  label: 'Type',           sortable: true  },
    { key: 'sessionCount', label: 'Conversations',   sortable: true  },
    { key: 'totalCost',    label: 'Total Cost',     sortable: true  },
    { key: 'addedToCart',  label: 'Cart',           sortable: true  },
    { key: 'checkedOut',   label: 'Checkout',       sortable: true  },
    { key: 'lastSeen',     label: 'Last Seen',      sortable: true  },
  ]

  return (
    <div>
      {/* KPI row */}
      <Grid container spacing={2} style={{ marginBottom: 20 }}>
        {kpis.map(function(kpi) {
          var pct = totalUsers > 0 ? Math.min(100, (kpi.value / totalUsers) * 100) : 0
          return (
            <Grid item xs={12} sm={6} md key={kpi.label}>
              <Paper className={classes.kpiCard} elevation={0}>
                <Typography className={classes.kpiValue} style={{ color: kpi.color }}>
                  {loading ? '…' : kpi.value}
                </Typography>
                <Typography className={classes.kpiLabel}>{kpi.label}</Typography>
                <div className={classes.kpiBar}>
                  <div style={{ height: '100%', width: (loading ? 0 : pct) + '%', borderRadius: 999, background: kpi.color, transition: 'width 0.6s ease' }} />
                </div>
              </Paper>
            </Grid>
          )
        })}
      </Grid>

      {/* Table */}
      <Paper elevation={0} style={{ border: '1px solid #e8ecf4', borderRadius: 12, overflow: 'hidden' }}>
        {/* Controls */}
        <div className={classes.controls}>
          <TextField
            className={classes.search}
            size="small"
            variant="outlined"
            placeholder="Search by name, ID, consumer ID…"
            value={search}
            onChange={function(e) { setSearch(e.target.value) }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 16, color: '#aaa' }} /></InputAdornment>
            }}
          />
          <Typography className={classes.count}>{filtered.length} user{filtered.length !== 1 ? 's' : ''}</Typography>
          <Button
            className={classes.exportBtn}
            startIcon={<GetAppIcon style={{ fontSize: 14 }} />}
            endIcon={<KeyboardArrowDownIcon style={{ fontSize: 14 }} />}
            onClick={function(e) { setExportAnchor(e.currentTarget) }}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={function() { setExportAnchor(null) }}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={function() { exportCSV(filtered); setExportAnchor(null) }}>
              <span style={{ fontSize: 13 }}>CSV <span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>Google Sheets, Excel</span></span>
            </MenuItem>
            <MenuItem onClick={function() { exportJSON(filtered); setExportAnchor(null) }}>
              <span style={{ fontSize: 13 }}>JSON <span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>pandas, Python, APIs</span></span>
            </MenuItem>
            <MenuItem onClick={function() { exportExcel(filtered); setExportAnchor(null) }}>
              <span style={{ fontSize: 13 }}>Excel (.xlsx) <span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>Microsoft Excel, Power BI</span></span>
            </MenuItem>
          </Menu>
        </div>

        {/* Table */}
        <div className={classes.tableWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {COLS.map(function(col) {
                  return (
                    <th
                      key={col.key}
                      className={col.sortable ? classes.th : classes.thNoSort}
                      onClick={col.sortable ? function() { handleSort(col.key) } : undefined}
                      style={Object.assign({ textAlign: col.key === 'sessionCount' ? 'center' : 'left' }, col.colStyle || {})}
                    >
                      {col.label}
                      {col.sortable && <SortIcon sortKey={sortKey} col={col.key} sortDir={sortDir} />}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={COLS.length} style={{ padding: 32, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={COLS.length} style={{ padding: '48px 20px', textAlign: 'center' }}>
                    <Typography style={{ fontSize: 28, marginBottom: 8 }}>👤</Typography>
                    <Typography style={{ fontSize: 13, color: '#aaa' }}>No users found</Typography>
                  </td>
                </tr>
              ) : filtered.map(function(u, i) {
                var displayName = [u.firstName, u.lastName].filter(Boolean).join(' ') || '—'
                var initials = u.isAnonymous ? '?' : (u.firstName ? u.firstName[0].toUpperCase() : '?')
                var costColor = u.totalCost >= 0.15 ? '#FB7185' : u.totalCost >= 0.05 ? '#d97706' : '#34D399'
                var isExpanded = expandedUsers.has(u.userId)

                return (
                  <React.Fragment key={u.userId}>
                    <tr className={i % 2 === 0 ? classes.trEven : classes.trOdd}>
                      {/* Expand toggle */}
                      <td className={classes.td} style={{ padding: '0 4px', width: 36 }}>
                        <IconButton size="small" onClick={function() {
                          setExpandedUsers(function(prev) {
                            var next = new Set(prev)
                            if (next.has(u.userId)) { next.delete(u.userId) } else { next.add(u.userId) }
                            return next
                          })
                        }}>
                          {isExpanded
                            ? <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
                            : <KeyboardArrowRightIcon style={{ fontSize: 18 }} />}
                        </IconButton>
                      </td>

                      {/* Name */}
                      <td className={classes.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className={classes.avatar} style={{
                            background: u.isAnonymous ? 'rgba(148,163,184,0.12)' : 'rgba(99,102,241,0.12)',
                            color: u.isAnonymous ? '#94A3B8' : '#818CF8',
                            border: '1px solid ' + (u.isAnonymous ? 'rgba(148,163,184,0.2)' : 'rgba(99,102,241,0.2)'),
                          }}>
                            {initials}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a2540' }}>{displayName}</div>
                            {!u.isAnonymous && u.consumerId && (
                              <div style={{ fontSize: 11, color: '#777', fontFamily: 'monospace' }}>{shortId(u.consumerId)}</div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* ID */}
                      <td className={classes.td}>
                        <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#333' }}>{shortId(u.userId)}</span>
                      </td>

                      {/* Device ID */}
                      <td className={classes.td}>
                        <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#333' }}>{u.deviceId ? shortId(u.deviceId) : '—'}</span>
                      </td>

                      {/* Type */}
                      <td className={classes.td}>
                        <BoolChip
                          value={!u.isAnonymous}
                          trueLabel="Authenticated"
                          falseLabel="Anonymous"
                          trueColor="#818CF8"
                          trueBg="rgba(99,102,241,0.1)"
                        />
                      </td>

                      {/* Sessions */}
                      <td className={classes.td} style={{ fontWeight: 600, color: '#1a2540', textAlign: 'center' }}>
                        {u.sessionCount}
                      </td>

                      {/* Total cost */}
                      <td className={classes.td}>
                        <span style={{ fontWeight: 700, color: costColor, fontFamily: 'monospace' }}>{fmtCost(u.totalCost)}</span>
                      </td>

                      {/* Cart */}
                      <td className={classes.td} style={{ textAlign: 'center' }}>
                        {u.cartSessionCount > 0
                          ? <Chip size="small" className={classes.chip}
                              label={'✓ ' + u.cartSessionCount + (u.cartSessionCount === 1 ? ' conversation' : ' conversations')}
                              style={{ background: 'rgba(52,211,153,0.12)', color: '#34D399' }} />
                          : <span style={{ fontSize: 13, color: '#ccc' }}>—</span>}
                      </td>

                      {/* Checkout */}
                      <td className={classes.td} style={{ textAlign: 'center' }}>
                        {u.checkoutSessionCount > 0
                          ? <Chip size="small" className={classes.chip}
                              label={'✓ ' + u.checkoutSessionCount + (u.checkoutSessionCount === 1 ? ' conversation' : ' conversations')}
                              style={{ background: 'rgba(251,191,36,0.1)', color: '#d97706' }} />
                          : <span style={{ fontSize: 13, color: '#ccc' }}>—</span>}
                      </td>

                      {/* Last seen */}
                      <td className={classes.td} style={{ fontSize: 12, color: '#333', whiteSpace: 'nowrap' }}>
                        {u.lastSeen ? format(u.lastSeen, 'MMM d, HH:mm') : '—'}
                      </td>
                    </tr>

                    {isExpanded && u.sessions.map(function(sess, si) {
                      var sCostColor = (sess.totalCost || 0) >= 0.15 ? '#FB7185' : (sess.totalCost || 0) >= 0.05 ? '#d97706' : '#34D399'
                      return (
                        <tr key={sess.id || si} style={{ background: '#f4f6ff', borderLeft: '3px solid #818CF8' }}>
                          <td className={classes.td} style={{ padding: '0 4px', width: 36 }}></td>
                          <td className={classes.td}>
                            <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#6366f1', paddingLeft: 12 }}>
                              {shortId(sess.id)}
                            </span>
                          </td>
                          <td className={classes.td}></td>
                          <td className={classes.td}>
                            <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#444' }}>{sess.deviceId ? shortId(sess.deviceId) : '—'}</span>
                          </td>
                          <td className={classes.td}></td>
                          <td className={classes.td} style={{ textAlign: 'center', color: '#333', fontSize: 12 }}>1</td>
                          <td className={classes.td}>
                            <span style={{ fontFamily: 'monospace', color: sCostColor }}>{fmtCost(sess.totalCost || 0)}</span>
                          </td>
                          <td className={classes.td} style={{ textAlign: 'center' }}>
                            <BoolChip value={sess.addedToCart} />
                          </td>
                          <td className={classes.td} style={{ textAlign: 'center' }}>
                            <BoolChip value={sess.checkedOut} trueColor="#d97706" trueBg="rgba(251,191,36,0.1)" />
                          </td>
                          <td className={classes.td} style={{ fontSize: 12, color: '#333', whiteSpace: 'nowrap' }}>
                            {sess.startTime ? format(sess.startTime, 'MMM d, HH:mm') : '—'}
                          </td>
                        </tr>
                      )
                    })}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </Paper>
    </div>
  )
}
